import { ActionTree } from 'vuex';
import { getProductInformation } from '@/api/product-information.api';
import { getProductMappingInformation } from '@/api/product-mapping-information.api';
import { ProductInformationState, ProductInformation, AtmReward, Qualifiers } from './types';
import { RootState } from '../types';

const AVERAGE_BALANCE_IN_LANDING_PAGE = 5000;

const getProductQualifierDescriptions = (levelOneQualRewardGroup: any): Qualifiers => {
  const qualifierDescriptionList: any[] = [];
  let minimumTransactionAmount = '';
  levelOneQualRewardGroup.qualifiers.forEach((qualifier: any) => {
    if (qualifier._class.includes('AchQualifier')) {
      const achTransactionType = qualifier.transactionType;
      switch (achTransactionType) {
        case 'ACH_DEBIT': {
          qualifierDescriptionList.push({ description: 'At least ' + qualifier.globalMinimumTransactionCount + ' ACH payment transaction(s)' });
          break;
        }
        case 'ACH_CREDIT': {
          qualifierDescriptionList.push({ description: 'At least ' + qualifier.globalMinimumTransactionCount + ' ACH credit transaction(s)' });
          break;
        }
        case 'BILL_PAY': {
          qualifierDescriptionList.push({ description: 'At least ' + qualifier.globalMinimumTransactionCount + ' bill pay(s)' });
          break;
        }
        case 'ACH_DEBIT_OR_ACH_CREDIT': {
          qualifierDescriptionList.push({ description: 'At least ' + qualifier.globalMinimumTransactionCount + ' ACH payment or ACH credit transaction(s)' });
          break;
        }
        case 'ACH_DEBIT_OR_BILL_PAY': {
          qualifierDescriptionList.push({ description: 'At least ' + qualifier.globalMinimumTransactionCount + ' ACH payment or bill pay transaction(s)' });
          break;
        }
        case 'ACH_CREDIT_OR_BILL_PAY': {
          qualifierDescriptionList.push({ description: 'At least ' + qualifier.globalMinimumTransactionCount + ' ACH credit or bill pay transaction(s)' });
          break;
        }
        case 'ACH_DEBIT_OR_ACH_CREDIT_OR_BILL_PAY': {
          qualifierDescriptionList.push({ description: 'At least ' + qualifier.globalMinimumTransactionCount + ' bill pay, ACH payment, or ACH credit transaction(s)' });
          break;
        }
      }
    } else if (qualifier._class.includes('CreditCardQualifier')) {
      minimumTransactionAmount = qualifier.minimumTransactionAmount;
      if (qualifier.minimumTransactionAmount === '0.00') {
        qualifierDescriptionList.push({ description: 'At least ' + qualifier.minimumTransactionCount + ' credit card purchases' });
      } else {
        qualifierDescriptionList.push({ description: 'At least ' + qualifier.minimumTransactionCount + ' credit card purchases, each $' + qualifier.minimumTransactionAmount + ' or more' });
      }
    } else if (qualifier._class.includes('CreditOrDebitCardQualifier')) {
      minimumTransactionAmount = qualifier.minimumTransactionAmount;
      if (qualifier.minimumTransactionAmount === '0.00') {
        qualifierDescriptionList.push({ description: 'At least a combined total of ' + qualifier.minimumTransactionCount + ' debit card purchases or credit card purchases' });
      } else {
        qualifierDescriptionList.push({ description: 'At least a combined total of ' + qualifier.minimumTransactionCount + ' debit card purchases, each $' + qualifier.minimumTransactionAmount +
          ' or more, or credit card purchases, each $' + qualifier.minimumTransactionAmount + ' or more' });
      }
    } else if (qualifier._class.includes('ElectronicStatementQualifier')) {
      qualifierDescriptionList.push({ description: 'Be enrolled in and agree to receive e-statements for your checking account' });
    } else if (qualifier._class.includes('IbLoginQualifier')) {
      qualifierDescriptionList.push({ description: 'Be enrolled in and log into online banking' });
    } else if (qualifier._class.includes('MinimumBalanceQualifier')) {
      qualifierDescriptionList.push({ description: 'Maintain a minimum balance of $' + qualifier.minimumBalanceAmount + ' in your checking account' });
    } else if (qualifier._class.includes('PosQualifier')) {
      minimumTransactionAmount = qualifier.minimumTransactionAmount;
      if (qualifier.minimumTransactionAmount === '0.00') {
        qualifierDescriptionList.push({ description: 'At least ' + qualifier.minimumTransactionCount + ' POS transaction(s)' });
      } else {
        qualifierDescriptionList.push({ description: 'At least ' + qualifier.minimumTransactionCount + ' POS transaction(s), each $' + qualifier.minimumTransactionAmount + ' or more' });
      }
    }
  });

  return {
    minTransactionAmount: minimumTransactionAmount,
    qualifierDescriptions: qualifierDescriptionList
  };
}

const getAtmReward = (levelOneQualRewardGroup: any): AtmReward => {
  const rewardList = levelOneQualRewardGroup.rewards.filter((reward: any) => reward._class.includes('AtmReward'));
  if (rewardList.length > 0) {
    const atmReward: any = rewardList[0];
    let atmRewardDescription = 'UNLIMITED'
    if (atmReward.maxTotalRefundAmountPerAccount !== null) {
      atmRewardDescription = 'UP TO $' + atmReward.maxTotalRefundAmountPerAccount;
    }
    return {
      productContains: true,
      description: atmRewardDescription,
      maxTotalRefund: atmReward.maxTotalRefundAmountPerAccount !== null ? atmReward.maxTotalRefundAmountPerAccount : '',
      maxRefundAmount: atmReward.maxRefundAmountPerTransaction !== null ? atmReward.maxRefundAmountPerTransaction : '',
      transactionLimit: atmReward.transactionCountLimit !== null ? atmReward.transactionCountLimit : undefined
    };
  } else {
    return {
      productContains: false,
      description: '',
      maxTotalRefund: '',
      maxRefundAmount: '',
      transactionLimit: undefined
    };
  }
}

export const actions: ActionTree<ProductInformationState, RootState> = {
  fetchData({ commit }, params: any) {

    return getProductMappingInformation(params.fiId, params.productShortname)
      .then((productMappingInformationResponse) => {
        const productMappingId = productMappingInformationResponse.data.data[0].externalProductMappingId;
        return getProductInformation(params.fiId, productMappingId)
          .then((response) => {
            let calculatedMaxReward = 0;
            let qualAprValue = '';
            let nonQualAprValue = '';
            let cashBackRateValue = '';
            let tier1CapValue = '';
            let tier2QualAprValue = '';
            let tier2CapValue = '';            
            let purchaseLimitValue = '';
            let levelOneQualRewardGroup: any = {};
            if (params.productShortname == 'cash') {
              // all of these filter calls will only return 1 item, so we reference with [0]
              levelOneQualRewardGroup = response.data.data.qualRewardGroups.filter((qualRewardGroup: any) => qualRewardGroup.level == 1)[0];
              const levelOneInterestReward = levelOneQualRewardGroup.rewards.filter((reward: any) => reward._class == 'com.bancvue.rcx.config.product.rewards.api.InterestReward')[0];
              const levelZeroQualRewardGroup = response.data.data.qualRewardGroups.filter((qualRewardGroup: any) => qualRewardGroup.level == 0)[0];
              const levelZeroInterestReward = levelZeroQualRewardGroup.rewards.filter((reward: any) => reward._class == 'com.bancvue.rcx.config.product.rewards.api.InterestReward')[0];
              // when calculating max reward, apr is a percentage, so we divide by 100 before multiplying by average balance
              calculatedMaxReward = Number(levelOneInterestReward.tier1.apr / 100) * AVERAGE_BALANCE_IN_LANDING_PAGE;
              qualAprValue = Number(levelOneInterestReward.tier1.apr).toFixed(2);
              nonQualAprValue = Number(levelZeroInterestReward.tier1.apr).toFixed(2);
              tier1CapValue = Number(levelOneInterestReward.tier1.cap).toLocaleString();
              tier2QualAprValue = Number(levelOneInterestReward.tier2.apr).toFixed(2);
              tier2CapValue = Number(levelOneInterestReward.tier2.cap).toLocaleString();
            } else {
              // all of these filter calls will only return 1 item, so we reference with [0]
              levelOneQualRewardGroup = response.data.data.qualRewardGroups.filter((qualRewardGroup: any) => qualRewardGroup.level == 1)[0];
              const levelOneCashBackReward = levelOneQualRewardGroup.rewards.filter((reward: any) => reward._class == 'com.bancvue.rcx.config.product.rewards.api.CashBackReward')[0];
              cashBackRateValue = levelOneCashBackReward.cashBackRate;
              calculatedMaxReward = Number(levelOneCashBackReward.cashBackRate) * Number(levelOneCashBackReward.purchaseLimit) * 12 / 100;
              qualAprValue = Number(levelOneCashBackReward.cashBackRate).toFixed(2);
              purchaseLimitValue = Number(levelOneCashBackReward.purchaseLimit).toFixed(2);
            }
            const qualifiers: any = getProductQualifierDescriptions(levelOneQualRewardGroup);
            const payload: ProductInformation = {
              id: response.data.data.id,
              fiProductName: response.data.data.productMapping.fiProductName,
              productType: response.data.data.productMapping.productType,
              accountType: response.data.data.productMapping.accountType,
              qualApr: qualAprValue,
              nonQualApr: nonQualAprValue,
              cashBackRate: cashBackRateValue,
              tier1Cap: tier1CapValue,
              tier2Cap: tier2CapValue,
              tier2QualApr: tier2QualAprValue,
              qualifierDescriptions: qualifiers.qualifierDescriptions,
              minTransactionAmount: qualifiers.minTransactionAmount,
              atmReward: getAtmReward(levelOneQualRewardGroup),
              maxReward: Number(calculatedMaxReward.toFixed(0)),
              purchaseLimit: purchaseLimitValue
            };
            commit('productInformationLoaded', payload);
          }, (error) => {
            commit('productInformationError', error);
          });
      });
  }
};


export default actions;
